import { type FC, useEffect } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import {
  useGetEnrollmentGetStartedData,
  useSubmitEnrollmentGetStartedData,
  type EnrollmentGetStartedSubmitInput,
} from '@egw/api'
import { GenericError } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import { CUSTOMER_SERVICE_PHONE_NUMBER, CUSTOMER_SERVICE_PHONE_NUMBER_DISPLAY, WEALTH_ROUTES } from '@egw/constants'
import { ROUTES } from '../../../constants'
import { ContentBlock, PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'

const log = new Logger('Enrollment - Mode Selection')

export const GetStartedPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()

  const { data, isLoading, error } = useGetEnrollmentGetStartedData()
  const { mutate: submit, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentGetStartedData()

  useValidateRoute(data?.path)

  const handleLinkClick = async (input: EnrollmentGetStartedSubmitInput): Promise<void> => {
    try {
      const response = await submit(input)
      if (response?.path) {
        navigate(response.path)
      }
    } catch (err) {
      log.error('Error submitting enrollment intro data', err)
    }
  }

  return (
    <EnrollmentLayout title="Get Started">
      <div className="egw-container-enroll h-full flex flex-col">
        {/* Loading initial data to make sure the user has access to this screen and retrieve data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error && !submitError ? (
          <>
            <PageHeader title={t('investments.mode-selection.title')} />
            <p className="mb-sm typo-primary-sm-regular text-text-meta">
              {t('investments.mode-selection.description')}
            </p>
            <ContentBlock
              title={t('investments.mode-selection.self_guided_title')}
              content={t('investments.mode-selection.self_guided_description')}
              isLink
              isLoading={isSubmitting}
              onClick={() =>
                void handleLinkClick({
                  proceedGetStarted: true,
                })
              }
            />
            <ContentBlock
              title={t('investments.mode-selection.with_an_advisor_title')}
              content={t('investments.mode-selection.with_an_advisor_description')}
              className="mb-xs"
            />
            <p className="typo-primary-sm-regular text-text-meta">
              {t('common.call')}{' '}
              <a
                href={`tel:${CUSTOMER_SERVICE_PHONE_NUMBER}`}
                title={t('account_beneficiaries.call')}
                rel="noopener"
                className="text-text-action hover:underline typo-primary-sm-semibold"
              >
                {CUSTOMER_SERVICE_PHONE_NUMBER_DISPLAY}
              </a>
            </p>
          </>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {!isLoading && !isSubmitting && (error ?? submitError) ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentLayout>
  )
}
